// GCCAdvisory.jsx
import React, { useRef, useEffect, useState } from 'react';
import './GCCAdvisory.css';
import gcc from "../../../assets/services/gcc.png";

import { FiTrendingUp, FiBarChart2, FiLayers, FiCpu } from 'react-icons/fi';
import {
  IoRocketOutline,
  IoStatsChartOutline,
  IoServerOutline,
  IoFlashOutline,
  IoBusinessOutline,
  IoPeopleOutline,
  IoGlobeOutline,
  IoTrendingUpOutline,
  IoShieldCheckmarkOutline,
  IoBulbOutline,
  IoGitBranchOutline,
} from 'react-icons/io5';
import { MdOutlineAnalytics, MdOutlineAutoAwesome } from 'react-icons/md';

/* ── Data ─────────────────────────────────────────────────── */
const deliverables = [
  {
    title: "GCC Strategy & Roadmap",
    description: "Define a clear GCC vision aligned with your enterprise transformation goals, growth strategy, and global operating model.",
    icon: <IoGlobeOutline />,
  },
  {
    title: "Location & Expansion Advisory",
    description: "Identify the right geography, talent ecosystem, infrastructure, and cost optimization opportunities for sustainable growth.",
    icon: <IoBusinessOutline />,
  },
  {
    title: "Operating Model Design",
    description: "Design scalable operating models covering governance, delivery structures, functional alignment, and cross-border collaboration.",
    icon: <FiLayers />,
  },
  {
    title: "Talent & Capability Transformation",
    description: "Build future-ready teams with the right mix of leadership, digital skills, engineering capabilities, and innovation culture.",
    icon: <IoPeopleOutline />,
  },
  {
    title: "AI, Automation & Digital Enablement",
    description: "Accelerate GCC maturity through AI-driven operations, automation frameworks, analytics, and intelligent enterprise platforms.",
    icon: <MdOutlineAutoAwesome />,
  },
  {
    title: "Governance & Performance Excellence",
    description: "Implement governance structures, KPIs, risk controls, compliance frameworks, and operational excellence practices.",
    icon: <IoShieldCheckmarkOutline />,
  },
];

const outcomes = [
  { text: "Faster Global Expansion",              icon: <IoRocketOutline /> },
  { text: "Improved Operational Efficiency",      icon: <IoStatsChartOutline /> },
  { text: "Scalable Delivery Models",             icon: <IoServerOutline /> },
  { text: "Reduced Operational Costs",            icon: <IoFlashOutline /> },
  { text: "Stronger Governance & Compliance",     icon: <IoShieldCheckmarkOutline /> },
  { text: "Accelerated Digital Transformation",   icon: <IoTrendingUpOutline /> },
  { text: "Enhanced Innovation Capability",       icon: <IoBulbOutline /> },
  { text: "AI-Driven Operational Excellence",     icon: <MdOutlineAnalytics /> },
];

const whyBairees = [
  { text: "Strategic business-first consulting approach",            icon: <FiTrendingUp /> },
  { text: "Deep expertise in GCC transformation and scaling",        icon: <FiBarChart2 /> },
  { text: "AI-led operational modernization frameworks",             icon: <FiCpu /> },
  { text: "Strong focus on innovation, agility, and outcomes",       icon: <IoBulbOutline /> },
  { text: "Scalable models tailored for global enterprises",         icon: <FiLayers /> },
  { text: "End-to-end advisory from strategy to execution",          icon: <IoGitBranchOutline /> },
];

const stats = [
  { value: 80,  suffix: "+",  label: "GCC transformations delivered" },
  { value: 35,  suffix: "%",  label: "Avg. cost efficiency gain" },
  { value: 12,  suffix: "+",  label: "Countries served" },
  { value: 100, suffix: "%",  label: "Strategy-to-execution coverage" },
];

/* ── Animated counter ──────────────────────────────────────── */
function useCountUp(target, duration = 1800, start = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setValue(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return value;
}

function StatCard({ value, suffix, label, delay, inView }) {
  const count = useCountUp(value, 1600, inView);
  return (
    <div className="gcc-stat" style={{ animationDelay: `${delay}ms` }}>
      <span className="gcc-stat__number">{count}{suffix}</span>
      <span className="gcc-stat__label">{label}</span>
    </div>
  );
}

/* ── Component ─────────────────────────────────────────────── */
const GCCAdvisory = () => {
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="gcc">

      {/* ── HERO ──────────────────────────────────────────── */}
      <section className="gcc-hero">
        <div className="gcc-hero__bg-grid" aria-hidden />
        <div className="gcc-hero__container">

          <div className="gcc-hero__content">
            <p className="gcc-eyebrow">GCC Advisory Services</p>
            <h1 className="gcc-hero__title">
              Global Capability<br />
              <span className="gcc-hero__title--gold">Centers</span>
            </h1>
            <p className="gcc-hero__tagline">Build. Scale. Lead Globally.</p>
            <p className="gcc-hero__desc">
              Bairees helps enterprises design, establish, and scale high-performing GCCs
              that drive innovation, operational excellence, and long-term business value —
              moving beyond traditional offshore delivery into intelligent, agile global hubs.
            </p>
            <div className="gcc-hero__actions">
             <a href="/contact"> <button className="gcc-btn gcc-btn--primary">Talk to Our GCC Experts <span className="arrow">→</span></button></a>
             <a href="/contact"> <button className="gcc-btn gcc-btn--ghost">View Framework</button></a>
            </div>
          </div>

          <div className="gcc-hero__aside">
            <div className="gcc-hero__card">
              <div className="gcc-hero__card-icon"><IoGlobeOutline /></div>
              <p className="gcc-hero__card-label">Strategy</p>
              <p className="gcc-hero__card-value">GCC Roadmap & Vision</p>
            </div>
            <div className="gcc-hero__card">
              <div className="gcc-hero__card-icon"><MdOutlineAutoAwesome /></div>
              <p className="gcc-hero__card-label">Intelligence</p>
              <p className="gcc-hero__card-value">AI-Driven Enablement</p>
            </div>
            <div className="gcc-hero__card">
              <div className="gcc-hero__card-icon"><IoShieldCheckmarkOutline /></div>
              <p className="gcc-hero__card-label">Governance</p>
              <p className="gcc-hero__card-value">Performance Excellence</p>
            </div>
          </div>

        </div>
      </section>

      {/* ── STATS BAR ─────────────────────────────────────── */}
      <section className="gcc-stats" ref={statsRef}>
        <div className="gcc-stats__inner">
          {stats.map((s, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div className="gcc-stats__divider" />}
              <StatCard {...s} delay={i * 150} inView={statsVisible} />
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* ── WHAT WE DELIVER ───────────────────────────────── */}
      <section className="gcc-section">
        <div className="gcc-section__container">
          <div className="gcc-section__header">
            <p className="gcc-eyebrow">Capabilities</p>
            <h2 className="gcc-section__title">What We Deliver</h2>
            <p className="gcc-section__subtitle">
              A full-spectrum advisory suite covering every stage of the GCC journey.
            </p>
          </div>
          <div className="gcc-deliverables">
            {deliverables.map((item, i) => (
              <div key={i} className="gcc-deliverable" style={{ animationDelay: `${i * 80}ms` }}>
                <div className="gcc-deliverable__icon">{item.icon}</div>
                <h3 className="gcc-deliverable__title">{item.title}</h3>
                <p className="gcc-deliverable__desc">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FRAMEWORK ─────────────────────────────────────── */}
      {/* <section className="gcc-framework">
        <div className="gcc-framework__container">
          <div className="gcc-framework__header">
            <p className="gcc-eyebrow gcc-eyebrow--light">Our Approach</p>
            <h2 className="gcc-framework__title">GCC Advisory Framework</h2>
          </div>
          <div className="gcc-framework__visual">
            <img src={gcc} alt="GCC Advisory Framework" className="gcc-framework__img" />
          </div>
        </div>
      </section> */}

      {/* ── OUTCOMES + WHY ────────────────────────────────── */}
      <section className="gcc-section gcc-section--alt">
        <div className="gcc-section__container">
          <div className="gcc-two-col">

            <div className="gcc-panel">
              <p className="gcc-eyebrow">What You Gain</p>
              <h2 className="gcc-panel__title">Business Outcomes</h2>
              <ul className="gcc-list">
                {outcomes.map((item, i) => (
                  <li key={i} className="gcc-list__item">
                    <span className="gcc-list__icon">{item.icon}</span>
                    <span className="gcc-list__text">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="gcc-panel">
              <p className="gcc-eyebrow">Our Edge</p>
              <h2 className="gcc-panel__title">Why Bairees</h2>
              <ul className="gcc-list">
                {whyBairees.map((item, i) => (
                  <li key={i} className="gcc-list__item">
                    <span className="gcc-list__icon">{item.icon}</span>
                    <span className="gcc-list__text">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* ── CTA — reuses app's next-section classes ────────── */}
      <section className="next-section">
        <div className="next-container">
          <div className="next-left">
            <h2 className="next-title">Transform Your GCC Into a Strategic Growth Engine</h2>
            <p className="next-subtitle">
              Intelligent, scalable, innovation-driven Global Capability Centers that
              accelerate enterprise transformation and global business success.
            </p>
            <div className="gcc-cta__chips">
              <span>✓ Strategy to execution</span>
              <span>✓ AI-driven operations</span>
              <span>✓ Global talent models</span>
              <span>✓ Scalable governance</span>
            </div>
          </div>
          <div className="next-right">
            <a href="/contact">
            <button className="next-btn">Start Here <span className="arrow">→</span></button>
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default GCCAdvisory;